import { forwardRef, useId, useState, type ChangeEvent } from 'react';
import { Textarea, type TextareaProps } from '../ui/Textarea';
import { FieldLabel } from './FieldLabel';

export interface TextareaFieldProps extends TextareaProps {
  required?: boolean;
  optional?: boolean;
  labelVariant?: 'bold' | 'caps';
  /** Figma Step-1 style: no visible label; label renders as the placeholder. */
  floating?: boolean;
  /** Show a live "n/maxLength" character counter (requires `maxLength`). */
  showCount?: boolean;
}

/** Textarea with a visible label and optional 0/500-style character counter. */
export const TextareaField = forwardRef<HTMLTextAreaElement, TextareaFieldProps>(
  function TextareaField(
    {
      label,
      required = false,
      optional = false,
      labelVariant = 'caps',
      floating = false,
      showCount = false,
      onChange,
      ...textareaProps
    },
    ref,
  ) {
    const textareaId = useId();
    const [count, setCount] = useState(
      String(textareaProps.defaultValue ?? textareaProps.value ?? '').length,
    );

    const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
      setCount(event.target.value.length);
      onChange?.(event);
    };

    const placeholder = floating
      ? (textareaProps.placeholder ?? `${label}${required ? '*' : ''}`)
      : textareaProps.placeholder;

    return (
      <div className="flex flex-col gap-1.5">
        {!floating && (
          <FieldLabel
            label={label}
            htmlFor={textareaId}
            required={required}
            optional={optional}
            variant={labelVariant}
          />
        )}
        <div className="relative">
          <Textarea
            ref={ref}
            id={textareaId}
            label={label}
            onChange={handleChange}
            {...textareaProps}
            className={`${floating ? 'placeholder:text-teal/70' : ''} ${textareaProps.className ?? ''}`.trim()}
            placeholder={placeholder}
          />
          {showCount && textareaProps.maxLength != null && (
            <span className="pointer-events-none absolute bottom-2 right-3 text-xs text-input-placeholder">
              {count}/{textareaProps.maxLength}
            </span>
          )}
        </div>
      </div>
    );
  },
);

TextareaField.displayName = 'TextareaField';
